import React from 'react';

/**
 * VirtualGridBoundary — catches the react-virtuoso "Maximum call stack
 * size exceeded" ResizeObserver loop that occasionally fires when the
 * data set changes rapidly (bulk uploads, realtime inserts) and renders
 * a non-virtualized fallback grid instead of crashing the whole page.
 */
interface Props {
  fallback: React.ReactNode;
  children: React.ReactNode;
  /** Reset the boundary when this key changes (e.g. items.length). */
  resetKey?: string | number;
}

interface State {
  crashed: boolean;
  lastKey?: string | number;
}

export class VirtualGridBoundary extends React.Component<Props, State> {
  state: State = { crashed: false, lastKey: this.props.resetKey };

  static getDerivedStateFromProps(next: Props, prev: State): State | null {
    if (next.resetKey !== prev.lastKey) {
      return { crashed: false, lastKey: next.resetKey };
    }
    return null;
  }

  static getDerivedStateFromError(err: unknown): Partial<State> {
    const msg = err instanceof Error ? err.message : String(err);
    if (/Maximum call stack size exceeded/i.test(msg)) {
      return { crashed: true };
    }
    // Re-throw non-Virtuoso errors by returning no state change.
    throw err;
  }

  componentDidCatch(err: unknown) {
    console.warn('[VirtualGridBoundary] falling back to plain grid:', err);
  }

  render() {
    if (this.state.crashed) return this.props.fallback;
    return this.props.children;
  }
}
